import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData, saveDossier } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import SaveLoader from "../shared/SaveLoader";
import ConfidenceLabel from "../shared/ConfidenceLabel";
import useConfidence from "../../hooks/useConfidence";
import "./InfosEnfant.css";
import toast from "react-hot-toast";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";

const RUBRIQUE = "infos_enfant";

const TABS = [
  { id: "identite", label: "Identité de l'enfant" },
  { id: "contact",  label: "Contact & Dossier" },
];

const EMPTY_IMAGES   = [];
const EMPTY_IDENTITE = {};
const EMPTY_CONTACT  = {};

const INITIAL_IDENTITE = { nom: "", prenoms: "", date_naissance: "", sexe: "" };
const INITIAL_CONTACT  = { telephone1: "", telephone2: "", numero_dossier: "" };

const InfosEnfant = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();
  const [saving, setSaving] = useState(false);

  const rawImages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || EMPTY_IMAGES);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];

  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;

  const savedIdentite = useSelector((s) => s.formulaires.data?.[id]?.infos_enfant?.identite || EMPTY_IDENTITE);
  const savedContact  = useSelector((s) => s.formulaires.data?.[id]?.infos_enfant?.contact  || EMPTY_CONTACT);

  const [identite, setIdentite] = useState({ ...INITIAL_IDENTITE, ...savedIdentite });
  const [contact,  setContact]  = useState({ ...INITIAL_CONTACT,  ...savedContact  });

  const identiteRef = useRef(identite);
  const contactRef  = useRef(contact);
  useEffect(() => { identiteRef.current = identite; }, [identite]);
  useEffect(() => { contactRef.current  = contact;  }, [contact]);

  useEffect(() => {
    setIdentite({ ...INITIAL_IDENTITE, ...savedIdentite });
    setContact({ ...INITIAL_CONTACT, ...savedContact });
  }, [id]);

  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [imgIndex,  setImgIndex]  = useState(0);

// APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav(RUBRIQUE);
  // ── Confidence ───────────────────────────────────────────────────────────────
  const { getConfidence } = useConfidence(id, RUBRIQUE);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleChange = (tab, field, value) => {
    if (tab === "identite") setIdentite((prev) => ({ ...prev, [field]: value }));
    if (tab === "contact")  setContact((prev)  => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, "identite", identiteRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "contact",  contactRef.current));
  };

  useEffect(() => {
    return () => { handleSave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    dispatch(updateFormData(id, RUBRIQUE, "identite", identite));
    dispatch(updateFormData(id, RUBRIQUE, "contact",  contact));
    try {
      const result = await dispatch(saveDossier());
      toast.success(result.message);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIdentite({ ...INITIAL_IDENTITE, ...savedIdentite });
    setContact({ ...INITIAL_CONTACT, ...savedContact });
  };

  if (!dossier) {
    return (
      <div className="ie-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="ie-page">
      {saving && <SaveLoader />}

      {/* ── Header ── */}
      <header className="ie-header">
        <div className="ie-header-left">
          <button className="ie-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="ie-title">Dossier {dossier.numero_dossier}</h1>
            <p className="ie-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="ie-save-btn" onClick={handleSaveAll} disabled={saving}>
          Sauvegarder les modifications
        </button>
      </header>

      {/* ── Corps ── */}
      <div className="ie-body">

        {/* ── Colonne gauche : image ── */}
        <aside className="ie-image-panel">
          <div className="ie-image-header">
            <span className="ie-rubrique-label">Informations de l'enfant</span>
            <span className="ie-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="ie-image-viewer">
            <img src={IMAGES[imgIndex]} alt={`Page ${imgIndex + 1}`} className="ie-image" />
          </div>
          <div className="ie-image-nav">
            <button className="ie-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
            <div className="ie-image-dots">
              {IMAGES.map((_, i) => (
                <span key={i} className={`ie-dot ${i === imgIndex ? "ie-dot--active" : ""}`} onClick={() => setImgIndex(i)} />
              ))}
            </div>
            <button className="ie-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
          </div>
        </aside>

        {/* ── Colonne droite : formulaire ── */}
        <main className="ie-form-panel">
          {/* Tabs */}
          <div className="ie-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`ie-tab ${activeTab === tab.id ? "ie-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Contenu onglet */}
          <div className="ie-tab-content">
            {activeTab === "identite" && (
              <div className="ie-fields">
                <div className="ie-field-group">
                  <label>
                    Nom de l'Enfant *
                    <ConfidenceLabel confidence={getConfidence("identite", "nom")} />
                  </label>
                  <input
                    type="text"
                    placeholder="Nom de famille"
                    value={identite.nom ?? ""}
                    onChange={(e) => handleChange("identite", "nom", e.target.value)}
                    style={{ borderColor: getConfidence("identite", "nom").color || undefined }}
                  />
                </div>
                <div className="ie-field-group">
                  <label>
                    Prénom(s) *
                    <ConfidenceLabel confidence={getConfidence("identite", "prenoms")} />
                  </label>
                  <input
                    type="text"
                    placeholder="Prénom(s)"
                    value={identite.prenoms ?? ""}
                    onChange={(e) => handleChange("identite", "prenoms", e.target.value)}
                    style={{ borderColor: getConfidence("identite", "prenoms").color || undefined }}
                  />
                </div>
                <div className="ie-row">
                  <div className="ie-field-group">
                    <label>
                      Date de Naissance *
                      <ConfidenceLabel confidence={getConfidence("identite", "date_naissance")} />
                    </label>
                    <input
                      type="date"
                      value={identite.date_naissance ?? ""}
                      onChange={(e) => handleChange("identite", "date_naissance", e.target.value)}
                      style={{ borderColor: getConfidence("identite", "date_naissance").color || undefined }}
                    />
                  </div>
                  <div className="ie-field-group ie-field-group--small">
                    <label>
                      Sexe *
                      <ConfidenceLabel confidence={getConfidence("identite", "sexe")} />
                    </label>
                    <select
                      value={identite.sexe ?? ""}
                      onChange={(e) => handleChange("identite", "sexe", e.target.value)}
                      style={{ borderColor: getConfidence("identite", "nom").color || undefined }}
                    >
                      <option value="">—</option>
                      <option value="m">M — Masculin</option>
                      <option value="f">F — Féminin</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "contact" && (
              <div className="ie-fields">
                <p className="ie-section-title">Téléphone(s) des Parents</p>
                <div className="ie-field-group">
                  <label>
                    Téléphone 1
                    <ConfidenceLabel confidence={getConfidence("contact", "telephone1")} />
                  </label>
                  <input
                    type="tel"
                    placeholder="+225 00 00 00 00 00"
                    value={contact.telephone1 ?? ""}
                    onChange={(e) => handleChange("contact", "telephone1", e.target.value)}
                    style={{ borderColor: getConfidence("contact", "telephone1").color || undefined }}
                  />
                </div>
                <div className="ie-field-group">
                  <label>
                    Téléphone 2
                    <ConfidenceLabel confidence={getConfidence("contact", "telephone2")} />
                  </label>
                  <input
                    type="tel"
                    placeholder="+225 00 00 00 00 00"
                    value={contact.telephone2 ?? ""}
                    onChange={(e) => handleChange("contact", "telephone2", e.target.value)}
                    style={{ borderColor: getConfidence("contact", "telephone2").color || undefined }}
                  />
                </div>
                <div className="ie-divider" />
                <p className="ie-section-title">Numéro Dossier</p>
                <div className="ie-field-group">
                  <label>
                    N° Dossier
                    <ConfidenceLabel confidence={getConfidence("contact", "numero_dossier")} />
                  </label>
                  <input
                    type="text"
                    placeholder="ex : 2026 / 0042"
                    value={contact.numero_dossier ?? ""}
                    onChange={(e) => handleChange("contact", "numero_dossier", e.target.value)}
                    style={{ borderColor: getConfidence("contact", "numero_dossier").color || undefined }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Actions ── */}
         {/*  <div className="ie-form-actions">
            <button className="ie-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="ie-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}

          {/* ── Navigation entre rubriques ── */}

<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />
        </main>
      </div>
    </div>
  );
};

export default InfosEnfant;